import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_USER_SCROLL_TIMEOUT_MS = 1500;
const PROGRAMMATIC_SCROLL_DURATION_MS = 500;

export interface UseAutoScrollToElementOptions {
  /** Whether auto-scrolling is currently enabled (e.g., when playing) */
  enabled?: boolean;
  /** How long to wait after user scroll before resuming auto-scroll (ms) */
  userScrollTimeoutMs?: number;
  /**
   * Pixels reserved at the bottom of the container (e.g. a gradient overlay).
   * Elements entering this zone trigger early scrolling and the target position
   * keeps them above this area.
   */
  bottomOffset?: number;
}

export interface UseAutoScrollToElementReturn<T extends HTMLElement> {
  /** Ref to attach to the scroll container */
  scrollContainerRef: React.RefObject<T | null>;
  /** Whether the user is currently scrolling (auto-scroll paused) */
  isUserScrolling: boolean;
  /** Scrolls the container to center the given element in view */
  scrollToElement: (element: HTMLElement) => void;
}

/**
 * Hook for auto-scrolling a container to keep an element centered in view.
 * Automatically pauses when user manually scrolls and resumes after a timeout.
 *
 * @example
 * ```tsx
 * const { scrollContainerRef, scrollToElement } = useAutoScrollToElement<HTMLDivElement>({
 *   enabled: isPlaying,
 * });
 *
 * // Attach ref to your scrollable container
 * <div ref={scrollContainerRef}>...</div>
 *
 * // Call when you want to scroll to an element
 * scrollToElement(targetElement);
 * ```
 */
export function useAutoScrollToElement<T extends HTMLElement = HTMLDivElement>({
  enabled = true,
  userScrollTimeoutMs = DEFAULT_USER_SCROLL_TIMEOUT_MS,
  bottomOffset = 0,
}: UseAutoScrollToElementOptions = {}): UseAutoScrollToElementReturn<T> {
  const scrollContainerRef = useRef<T>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticScrollRef = useRef(false);

  const handleScroll = useCallback(() => {
    // Ignore programmatic scrolls - only track user-initiated scrolls
    if (isProgrammaticScrollRef.current) return;

    setIsUserScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, userScrollTimeoutMs);
  }, [userScrollTimeoutMs]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const scrollToElement = useCallback(
    (element: HTMLElement) => {
      if (!scrollContainerRef.current || !enabled || isUserScrolling) return;

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const effectiveBottom = containerRect.bottom - bottomOffset;
      const isAboveView = elementRect.top < containerRect.top;
      const isBelowView = elementRect.bottom > effectiveBottom;

      if (isAboveView || isBelowView) {
        const effectiveHeight = containerRect.height - bottomOffset;
        const elementTop = element.offsetTop;
        const elementHeight = element.offsetHeight;

        // Position the element at ~35% from the top of the effective visible area
        const targetScrollTop = elementTop - effectiveHeight * 0.35 + elementHeight / 2;

        isProgrammaticScrollRef.current = true;
        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: "smooth",
        });

        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, PROGRAMMATIC_SCROLL_DURATION_MS);
      }
    },
    [enabled, isUserScrolling, bottomOffset],
  );

  return {
    scrollContainerRef,
    isUserScrolling,
    scrollToElement,
  };
}
